import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilState } from "recoil";
import CancelButton from "components/CancelButton";
import ConfirmBoxModal from "components/ConfirmBoxModal";
import SubmitHookFormTS from "components/SubmitHookFormTS";
import { IOfficeAppliancesType } from "../types";
import {
  addStateOfficeAppliancesType,
  editStateOfficeAppliancesType,
  rowEditOfficeAppliancesType,
  selectedRowsStateOfficeAppliancesType,
  officeAppliancesTypeSaving,
  officeAppliancesTypeFilterText,
} from "../atoms/atoms";
import useAddType from "../hooks/useAddType";
import useUpdateType from "../hooks/useUpdateType";
import TypeInput from "./TypeInput";

type TypeFormProps = {
  setRecent: React.Dispatch<React.SetStateAction<string>>;
};

export const TypeForm: React.FC<TypeFormProps> = ({ setRecent }) => {
  const [editData, setEditData] = useRecoilState(rowEditOfficeAppliancesType);
  const [isSaving, setIsSaving] = useRecoilState(officeAppliancesTypeSaving);
  const [isAddState, setIsAddState] = useRecoilState(
    addStateOfficeAppliancesType
  );
  const [isEditState, setIsEditState] = useRecoilState(
    editStateOfficeAppliancesType
  );
  const [, setSelectedRows] = useRecoilState(
    selectedRowsStateOfficeAppliancesType
  );
  const addType = useAddType();
  const updateType = useUpdateType();
  const [newData, setNewData] = useState<IOfficeAppliancesType>();
  const [saveModal, setSaveModal] = useState(false);
  const [changes, setChanges] = useState(false);
  const [, setFilterTextState] = useRecoilState(officeAppliancesTypeFilterText);

  const formMethods = useForm<IOfficeAppliancesType>({
    defaultValues: (editData as IOfficeAppliancesType) || {
      officeApplianceTypeId: 0,
      officeApplianceType1: "",
      createdBy: "",
      createdDate: "",
      modifiedBy: "",
      modifiedDate: "",
      isActive: true,
    },
  });

  const onSubmit = (data: IOfficeAppliancesType) => {
    const trimmedName = (data.officeApplianceType1 || "").trim();

    const base: IOfficeAppliancesType = (editData as IOfficeAppliancesType) || {
      officeApplianceTypeId: 0,
      createdBy: "",
      createdDate: new Date(),
      modifiedBy: "",
      modifiedDate: new Date(),
      isActive: true,
      officeApplianceType1: "",
    };

    const value: IOfficeAppliancesType = {
      ...base,
      ...data,
      officeApplianceTypeId: base.officeApplianceTypeId,
      officeApplianceType1: trimmedName,
    };

    setNewData(value);
    setSaveModal(true);
    setIsSaving(true);
  };

  const resetFormState = () => {
    formMethods.reset();
    formMethods.clearErrors();
    setEditData(null);
    setIsAddState(false);
    setIsEditState(false);
    setSelectedRows([]);
    setChanges(false);
  };

  const handleCancel = () => {
    resetFormState();
    setRecent("");
  };

  const handleSave = () => {
    if (!newData) return;
    if (isEditState) {
      updateType(newData);
    } else {
      addType(newData);
    }

    setFilterTextState("");
    setRecent("");

    resetFormState();
  };

  return (
    <>
      <div className="px-2 py-2 space-y-2 border-l-2 border-primary box-shadow">
        <FormProvider {...formMethods}>
          <form onSubmit={formMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
              <div className="ml-2 font-bold text-primary text-base">
                {isAddState ? "Add New Type" : "Edit Type"}
              </div>
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookFormTS
                  type="submit"
                  disabled={!changes}
                  buttonText={isSaving ? "Saving.." : "Save"}
                />
                <CancelButton
                  onClick={(event) => {
                    event.preventDefault();
                    handleCancel();
                  }}
                />
              </div>
            </div>

            <div className="mx-2 border-b-2 border-secondary" />
            <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
              <b>Note:</b> All fields with * are required to fill.
            </div>

            <div className="mt-2">
              <TypeInput
                data={editData as IOfficeAppliancesType | undefined}
                setChanges={setChanges}
              />
            </div>
          </form>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          qConfirmation={`Are you sure you want to ${
            isAddState ? "add" : "update"
          } this data?`}
          ModalTitle={isAddState ? "Add Type" : "Update Type"}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
          }}
          onClickFunction={() => {
            setSaveModal(false);
            handleSave();
            setIsSaving(false);
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};

export default TypeForm;
